// Real application state for the portal. Provides the `useApp()` context shape the screens
// consume (env, apiKey, partner, lang, codeDark, setters), backed by:
//   • a real environment switcher that repoints the /v2 base URL (config/environments.ts)
//   • a real authenticated session (partner api-key + trust flag), held in memory
//   • UI prefs (code theme, sample language) in localStorage
import { useState, useEffect, useMemo, useContext, type ReactNode } from 'react';

import { ENVIRONMENTS, ENVIRONMENT_IDS, defaultEnvironment, apiBaseUrl, type EnvironmentId } from '@/config/environments';
import { config } from '@/config';

import { AppCtx, Select } from '@/components/ds';
import { TEST_KEY, LIVE_KEY } from '@/data';
import type { AppContextValue, Session, PartnerType } from '@/lib/types';

// ---- Persistence -------------------------------------------------------------------------
// SECURITY: the partner api-key and session are held in memory only (React state) — never in
// session/localStorage. Only non-secret UI prefs (code theme, sample language) are persisted.
const PREFS_KEY = 'payouts.portal.prefs';

interface Prefs {
  lang: string;
  codeDark: boolean;
}

function readJson<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [env, setEnv] = useState<EnvironmentId>(() => defaultEnvironment(config.environment, window.location.hostname));
  const [auth, setAuth] = useState<Session | null>(null);
  const [prefs, setPrefs] = useState<Prefs>(() => readJson<Prefs>(localStorage, PREFS_KEY) || { lang: 'curl', codeDark: true });

  useEffect(() => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const isLive = !!ENVIRONMENTS[env].live;

  const ctx = useMemo<AppContextValue>(
    () => ({
      // environment (drives the real API base URL)
      env,
      envLabel: ENVIRONMENTS[env].label,
      isLive,
      apiBase: apiBaseUrl(env),
      setEnv,
      // authenticated session
      authed: !!(auth?.partnerName || auth?.companyName),
      hasKey: !!auth?.apiKey,
      apiKey: auth?.apiKey || (isLive ? LIVE_KEY : TEST_KEY),
      partner: (auth?.partner as PartnerType) || 'self-serve',
      companyName: auth?.companyName,
      partnerName: auth?.partnerName,
      email: auth?.email,
      portalToken: auth?.portalToken,
      signIn: (session: Session) => setAuth((a) => ({ ...(a || {}), ...session })),
      signOut: () => setAuth(null),
      setApiKey: (apiKey: string) => setAuth((a) => ({ ...(a || {}), apiKey })),
      setPartner: (partner: PartnerType) => setAuth((a) => ({ ...(a || {}), partner })),
      // UI prefs
      lang: prefs.lang,
      codeDark: prefs.codeDark,
      setLang: (lang: string) => setPrefs((p) => ({ ...p, lang })),
      setCodeDark: (codeDark: boolean) => setPrefs((p) => ({ ...p, codeDark })),
    }),
    [env, isLive, auth, prefs]
  );

  return <AppCtx.Provider value={ctx}>{children}</AppCtx.Provider>;
}

// ---- Environment switcher (top-bar control) ----------------------------------------------
export function EnvSwitcher({ width = 168 }: { width?: number }) {
  const ctx = useContext(AppCtx);
  if (!ctx) return null;
  const options = ENVIRONMENT_IDS.map((id) => ({ value: id, label: ENVIRONMENTS[id].label }));
  return <Select value={ctx.env} onChange={(v) => ctx.setEnv(v as EnvironmentId)} options={options} style={{ width, height: 38 }} />;
}
